import { useEffect, useState, useMemo } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";
import { useNavigate } from "react-router-dom";

export default function EmailTemplates() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { showModal } = useMessageModal();
  const { toCaptionCase } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);
  const [emailBodies, setEmailBodies] = useState<any[]>([]);
  const navigate = useNavigate();

  /* =========================
     FETCH DATA
  ========================= */

  useEffect(() => {
    Promise.all([
      fetch(`${BASE_URL}/admin/email-settings/email-templates`, {
        credentials: "include",
      }).then((r) => r.json()),

      fetch(`${BASE_URL}/admin/email-settings/email-bodies`, {
        credentials: "include",
      }).then((r) => r.json()),
    ]).then(([emailRes, bodyRes]) => {
      if (emailRes.success) {
        setData(emailRes.data);
      }

      if (bodyRes.success) {
        setEmailBodies(
          bodyRes.data.map((b: any) => ({
            id: b.id,
            label: b.name,
          })),
        );
      }
    });
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/email-settings/email-templates`, {
        credentials: "include",
      });

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);
        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );

        setActiveRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Email Templates Error:", error);
    }
  };

  /* =========================
     ADD / UPDATE
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/email-settings/email-templates`
          : `${BASE_URL}/admin/email-settings/email-templates/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success) {
        fetchTemplates();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message:
            modalMode === "add"
              ? "Public email added successfully."
              : "Public email updated successfully.",
        });
      }
    } catch (error) {
      console.error("Submit Error:", error);
    }
  };

  /* =========================
     DELETE (SOFT DELETE)
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Email Template",
      message: `Are you sure you want to delete "${row.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/email-settings/email-templates/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchTemplates();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Email template deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  /* =========================
     FORM FIELDS
  ========================= */

  const emailFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 2 },

      { key: "template_name", label: "Template Name", colSpan: 2 },

      { key: "email_subject", label: "Email Subject", colSpan: 2 },

      {
        key: "email_body_id",
        label: "Email Body",
        type: "select",
        options: emailBodies.map((b) => ({
          label: toCaptionCase(b.label),
          value: b.id,
        })),
        colSpan: 2,
      },

      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    ],
    [emailBodies],
  );

  /* =========================
     COLUMNS
  ========================= */

  const columns = [
    { key: "template_name", label: "Template Name" },
    { key: "email_subject", label: "Subject" },
    { key: "status", label: "Status" },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Email Templates"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
        extraButtons={[
          {
            label: "Create Body",
            onClick: () => navigate("/admin/email-bodies"),
          },
        ]}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: any) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.template_name}</h3>

                <p>
                  <strong>Subject:</strong> {item.email_subject}
                </p>

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {item.status?.charAt(0).toUpperCase() +
                      item.status?.slice(1)}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Email Template"
        fields={emailFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
